import { derived } from 'svelte/store';
import { createQuery, type Query, QueryClient } from '$lib/api/tanstack-query';

import type { GetActiveRobotReservationResponse } from '@viamrobotics/web-client/robotreservation/v1/robot_reservation';
import type { RobotReservationServiceClient } from '@viamrobotics/web-client/robotreservation/v1/robot_reservation.client';

import type { AuthContext } from '$lib/auth';

import { prefetchQuery } from '../prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from '../query-options';
import { tryReservationsKeys } from './query-keys';
import { isRunning, isSettingUpAgent } from './reservation-states';

type GetActiveReservationQuery = Pick<
  Query<
    GetActiveRobotReservationResponse,
    Error,
    GetActiveRobotReservationResponse,
    ReturnType<typeof tryReservationsKeys.active>
  >,
  'state'
>;

type GetActiveReservationRefetchInterval =
  | number
  | false
  | undefined
  | ((query: GetActiveReservationQuery) => number | false | undefined);

/**
 * Provides an easy way to determine the refetch interval when polling for active reservations.
 * - If the user has an active reservation, returns 1 second
 * - If the user does not have an active reservation, returns 10 seconds
 */
export const activeReservationRefetchInverval = ({
  state,
}: GetActiveReservationQuery) => {
  const ACTIVE_REFETCH_INTERVAL_MS = 1000;
  const INACTIVE_REFETCH_INTERVAL_MS = 10_000;

  if (!state.data) {
    return INACTIVE_REFETCH_INTERVAL_MS;
  }

  const isActive =
    isRunning(state.data.reservation) ||
    isSettingUpAgent(state.data.reservation);

  return isActive ? ACTIVE_REFETCH_INTERVAL_MS : INACTIVE_REFETCH_INTERVAL_MS;
};

/**
 * Creates a query to get the current user's active reservation, if one exists.
 *
 * @param refetchInterval The refetch interval to use. If none is passed, it will use `activeReservationRefetchInverval`.
 */
export const createGetActiveTryReservationQuery = (
  refetchInterval: GetActiveReservationRefetchInterval = activeReservationRefetchInverval
) => {
  const baseOptions = useAuthedQueryOptions('robotReservation');
  const queryOptions = derived([baseOptions], ([$baseOptions]) =>
    getActiveTryReservationQueryOptions($baseOptions, refetchInterval)
  );

  return createQuery(queryOptions);
};

export const prefetchGetActiveTryReservation = async (
  queryClient: QueryClient,
  client: RobotReservationServiceClient,
  authContext: AuthContext
) => {
  const baseAuthedOptions = getAuthedQueryOptions(client, authContext);
  const options = getActiveTryReservationQueryOptions(baseAuthedOptions, false);

  return prefetchQuery(queryClient, options);
};

const getActiveTryReservationQueryOptions = (
  {
    client,
    getRPCOptions,
    enabled,
  }: BaseQueryOptions<RobotReservationServiceClient>,
  refetchInterval: GetActiveReservationRefetchInterval
) => {
  return createQueryOptions({
    enabled,
    queryKey: tryReservationsKeys.active(),
    refetchInterval: (query) => {
      if (query.state.error ?? !refetchInterval) {
        return false;
      }

      return typeof refetchInterval === 'number'
        ? refetchInterval
        : refetchInterval(query);
    },
    queryFn: async () => {
      const rpcOptions = await getRPCOptions();
      const { response } = await client.getActiveRobotReservation(
        {},
        rpcOptions
      );
      return response;
    },
  });
};
